import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Plus } from 'lucide-react';
import { clsx } from 'clsx';
import { formatCurrency } from '@utils/formatters';
import { formatCompact, MONTHS } from './dashboardHelpers';
import ChartTooltip from './ChartTooltip';

const DashboardHero = ({ user, currentBalance, monthSummary, accounts, balanceData, heroChange, dashLoading, onNewTransaction }) => {
  const now = new Date();

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-primary-900/80 to-slate-900 text-white shadow-2xl">
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-primary-500/10 blur-2xl" />
      <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-primary-400/10 blur-2xl" />

      <div className="relative px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-white/60 text-sm font-medium">
              Olá, {user?.name?.split(' ')[0]} 👋
            </p>
            <p className="text-white/40 text-xs mt-0.5">
              {MONTHS[now.getMonth()]} {now.getFullYear()}
            </p>
          </div>
          <button onClick={onNewTransaction}
            className="flex items-center gap-2 bg-white text-primary-700 hover:bg-primary-50 transition-colors px-4 py-2 rounded-xl text-sm font-semibold shadow-lg">
            <Plus size={16} />
            Nova Transação
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 items-center">
          <div>
            <p className="text-white/50 text-sm font-medium mb-1">Patrimônio Total</p>
            <p className="text-5xl font-bold tracking-tight">
              {dashLoading ? (
                <span className="inline-block w-48 h-12 bg-white/10 rounded-xl animate-pulse" />
              ) : formatCurrency(currentBalance)}
            </p>

            {heroChange && (
              <div className={clsx(
                'flex items-center gap-2 mt-3',
                heroChange.amount >= 0 ? 'text-emerald-400' : 'text-red-400'
              )}>
                {heroChange.amount >= 0
                  ? <ArrowUpRight size={18} />
                  : <ArrowDownRight size={18} />}
                <span className="font-semibold text-sm">{formatCurrency(Math.abs(heroChange.amount))}</span>
                <span className="text-xs opacity-80">({Math.abs(heroChange.pct).toFixed(1)}% vs mês anterior)</span>
              </div>
            )}

            <div className="flex items-center gap-6 mt-5">
              <div>
                <p className="text-white/40 text-xs">Receitas</p>
                <p className="text-emerald-400 font-bold text-base">{formatCompact(monthSummary.income)}</p>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div>
                <p className="text-white/40 text-xs">Despesas</p>
                <p className="text-red-400 font-bold text-base">{formatCompact(monthSummary.expense)}</p>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div>
                <p className="text-white/40 text-xs">Saldo do Mês</p>
                <p className={clsx(
                  'font-bold text-base',
                  monthSummary.balance >= 0 ? 'text-emerald-400' : 'text-red-400'
                )}>
                  {formatCompact(monthSummary.balance)}
                </p>
              </div>
            </div>
          </div>

          <div className="hidden lg:block">
            <p className="text-white/40 text-xs mb-2">Evolução patrimonial (6 meses)</p>
            {balanceData.length > 1 ? (
              <ResponsiveContainer width="100%" height={90}>
                <AreaChart data={balanceData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                  <defs>
                    <linearGradient id="heroArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#fff" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#fff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" hide />
                  <YAxis hide domain={['auto', 'auto']} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="balance" name="Saldo"
                    stroke="rgba(255,255,255,0.8)" strokeWidth={2}
                    fill="url(#heroArea)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-20 flex items-center justify-center text-white/30 text-sm">
                Dados insuficientes
              </div>
            )}
          </div>
        </div>
      </div>

      {accounts.length > 0 && (
        <div className="flex items-center gap-3 px-6 py-3 border-t border-white/10 overflow-x-auto scrollbar-hide">
          {accounts.map(acc => (
            <div key={acc.accountId}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/15 transition-colors rounded-xl px-3 py-1.5 flex-shrink-0">
              <span className="text-base">{acc.icon}</span>
              <div>
                <p className="text-white/70 text-xs leading-none">{acc.name}</p>
                <p className={clsx(
                  'text-xs font-bold leading-tight mt-0.5',
                  acc.balance >= 0 ? 'text-white' : 'text-red-400'
                )}>
                  {formatCompact(acc.balance)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardHero;
